/**
 * `discourse` — a Discourse forum's RSS (sources.md "discourse"): latest topics, latest posts, a category
 * or a tag. No CORS → `relay-only`. One request returns full post HTML. `threadKey` = the topic id.
 */

import type { DiscourseConfig, RawItem, SourceClient, SourceFetchContext, SourceFetchResult } from '../contracts/sources.js';
import { DigesterError } from '../kernel/errors.js';
import { entryToItem, parseFeed } from './feed.js';
import { assertOk, contextConditionalHeaders, invalidInput, newestFirst, parseHttpUrl, validatorsOf } from './http.js';
import { FEED_ACCEPT } from './rss.js';

const MODES: readonly DiscourseConfig['mode'][] = ['latest-topics', 'latest-posts', 'category', 'tag'];
/** Path segments that start a Discourse route: whatever precedes them is the forum's base path. */
const ROUTES = new Set(['c', 'tag', 'tags', 'latest', 'latest.rss', 'posts', 'posts.rss', 't', 'top', 'new', 'categories']);

type Parsed = { baseUrl: string; mode: DiscourseConfig['mode']; category?: string; tag?: string };

export function parseDiscourseAddress(address: string, config: Partial<DiscourseConfig> | undefined): Parsed | null {
  const url = parseHttpUrl(config?.baseUrl ?? address);
  if (!url) return null;
  const parts = url.pathname.split('/').filter((p) => p !== '');
  const routeAt = parts.findIndex((p) => ROUTES.has(p));
  const baseParts = routeAt === -1 ? parts : parts.slice(0, routeAt);
  const route = routeAt === -1 ? [] : parts.slice(routeAt);
  const baseUrl = `${url.protocol}//${url.host}${baseParts.length ? `/${baseParts.join('/')}` : ''}`;
  let parsed: Parsed = { baseUrl, mode: 'latest-topics' };
  if (route[0] === 'c' && route.length >= 3) parsed = { baseUrl, mode: 'category', category: `${route[1]}/${(route[2] ?? '').replace(/\.rss$/, '')}` };
  else if ((route[0] === 'tag' || route[0] === 'tags') && route[1]) parsed = { baseUrl, mode: 'tag', tag: route[1].replace(/\.rss$/, '') };
  else if (route[0] === 'posts' || route[0] === 'posts.rss') parsed = { baseUrl, mode: 'latest-posts' };
  if (config?.mode && MODES.includes(config.mode)) {
    parsed = { baseUrl, mode: config.mode };
    if (config.mode === 'category') parsed.category = config.category ?? '';
    if (config.mode === 'tag') parsed.tag = config.tag ?? '';
  }
  if (parsed.mode === 'category' && !/^[^/\s]+\/\d+$/.test(parsed.category ?? '')) return null;
  if (parsed.mode === 'tag' && !parsed.tag) return null;
  return parsed;
}

export function discourseLocator(p: Parsed): string {
  const url = new URL(p.baseUrl);
  const hostPath = `${url.host}${url.pathname === '/' ? '' : url.pathname}`.toLowerCase();
  const qualifier = p.mode === 'category' ? `:${(p.category ?? '').toLowerCase()}` : p.mode === 'tag' ? `:${(p.tag ?? '').toLowerCase()}` : '';
  return `discourse:${hostPath}:${p.mode}${qualifier}`;
}

export function discourseFeedUrl(c: Pick<DiscourseConfig, 'baseUrl' | 'mode' | 'category' | 'tag'>): string {
  switch (c.mode) {
    case 'latest-topics':
      return `${c.baseUrl}/latest.rss`;
    case 'latest-posts':
      return `${c.baseUrl}/posts.rss`;
    case 'category':
      return `${c.baseUrl}/c/${c.category ?? ''}.rss`;
    case 'tag':
      return `${c.baseUrl}/tag/${encodeURIComponent(c.tag ?? '')}.rss`;
  }
}

/** The topic id from a topic or post link: `/t/<slug>/<id>[/<post>]`. */
export function topicIdOf(url: string | undefined): string | undefined {
  if (!url) return undefined;
  return /\/t\/(?:[^/]+\/)?(\d+)(?:\/\d+)?\/?(?:[?#].*)?$/.exec(url)?.[1];
}

export function discourseItems(xml: string, baseUrl: string): { title: string; items: RawItem[] } {
  const feed = parseFeed(xml, 'The Discourse feed');
  const items: RawItem[] = [];
  for (const entry of feed.entries) {
    const item = entryToItem(entry, baseUrl);
    if (!item) continue;
    const topic = topicIdOf(item.url);
    if (topic) item.threadKey = topic;
    items.push(item);
  }
  return { title: feed.title, items };
}

export const discourseClient: SourceClient<'discourse'> = {
  kind: 'discourse',
  defaultPullMinutes: 30,

  async resolve(input, runtime) {
    const parsed = parseDiscourseAddress(input.address, input.config);
    if (!parsed) throw invalidInput('Enter the forum URL, optionally a /c/<slug>/<id> category or /tag/<tag> page.');
    const config: DiscourseConfig = { kind: 'discourse', baseUrl: parsed.baseUrl, mode: parsed.mode };
    if (parsed.category) config.category = parsed.category;
    if (parsed.tag) config.tag = parsed.tag;
    const url = discourseFeedUrl(config);
    const response = await runtime.fetcher.fetch({ url, method: 'GET', headers: { Accept: FEED_ACCEPT }, route: 'relay-only' });
    assertOk(response, { credential: false, what: url, now: runtime.clock.now() });
    const { title } = discourseItems(response.text, parsed.baseUrl);
    if (!title && !response.text.includes('<rss')) throw new DigesterError('parse_failed', 'That forum does not offer an RSS feed here.');
    return { kind: 'discourse', locator: discourseLocator(parsed), name: title || new URL(parsed.baseUrl).host, config, requiredSecrets: [] };
  },

  async fetch(ctx: SourceFetchContext<'discourse'>): Promise<SourceFetchResult> {
    const c = ctx.source.config;
    const url = discourseFeedUrl(c);
    const response = await ctx.runtime.fetcher.fetch({ url, method: 'GET', headers: { Accept: FEED_ACCEPT, ...contextConditionalHeaders(ctx) }, route: 'relay-only' });
    assertOk(response, { credential: false, what: ctx.source.name || url, now: ctx.runtime.clock.now() });
    const cursor = ctx.cursor ?? {};
    if (response.status === 304) return { items: [], cursor, notModified: true };
    const items = newestFirst(discourseItems(response.text, c.baseUrl).items, ctx.maxItems);
    const newest = items.find((i) => i.publishedAt !== undefined);
    return { items, cursor: newest ? { ...cursor, newestAt: newest.publishedAt } : cursor, ...validatorsOf(response) };
  },
};
